import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
    Trash2,
    Eye,
    X,
    ArrowLeft,
    Edit,
    Plus,
    CircleStar,
    Palette,
    RotateCcw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../auth/Auth";
import { uploadFile, deleteRequest, postRequest } from "../../routes/Routes";
import { loadTemplates } from "../../common/Common";
import { toast } from "react-toastify";

/* =============================== Utils =============================== */
const uid = () => Math.random().toString(36).slice(2, 9);
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const DEFAULT_BG_GRAD = "from-pink-100 via-purple-50 to-blue-100";
const DEFAULT_ACCENT_GRAD = "from-pink-500 to-purple-500";
const DEFAULT_TEXT = "#1f2937";

/* Default block layout (percentage units for x/y/width) */
const defaultLayout = () => [
    {
        id: uid(),
        type: "heading",
        text: "Happy",
        x: 10,
        y: 10,
        w: 80,
        rotate: 0,
        style: {
            color: "#db2777",
            fontSize: 64,
            fontWeight: 800,
            lineHeight: 1.05,
            textAlign: "center",
            italic: false,
            uppercase: false,
            shadow: true,
            gradient: "from-pink-500 via-purple-500 to-blue-500",
        },
    },
    {
        id: uid(),
        type: "subheading",
        text: "Birthday!",
        x: 10,
        y: 18,
        w: 80,
        rotate: 0,
        style: {
            color: "#ec4899",
            fontSize: 60,
            fontWeight: 800,
            lineHeight: 1.05,
            textAlign: "center",
            italic: false,
            uppercase: false,
            shadow: true,
            gradient: "from-purple-500 via-pink-500 to-red-500",
        },
    },
    {
        id: uid(),
        type: "receiver",
        text: "Dear {receiverName}",
        x: 10,
        y: 32,
        w: 80,
        rotate: 0,
        style: {
            color: DEFAULT_TEXT,
            fontSize: 40,
            fontWeight: 700,
            lineHeight: 1.2,
            textAlign: "center",
            italic: false,
            uppercase: false,
            shadow: false,
        },
    },
    {
        id: uid(),
        type: "message",
        text: '"{message}"',
        x: 10,
        y: 40,
        w: 80,
        rotate: 0,
        style: {
            color: "#374151",
            fontSize: 28,
            fontWeight: 500,
            lineHeight: 1.5,
            textAlign: "center",
            italic: true,
            uppercase: false,
            shadow: false,
        },
    },
    {
        id: uid(),
        type: "sender",
        text: "{senderName} 💝",
        x: 10,
        y: 63,
        w: 80,
        rotate: 0,
        style: {
            color: "#7c3aed",
            fontSize: 32,
            fontWeight: 800,
            lineHeight: 1.1,
            textAlign: "center",
            italic: false,
            uppercase: false,
            shadow: false,
        },
    },
];

/* Resolve placeholders in text */
const resolveText = (raw, vars) =>
    String(raw ?? "")
        .replaceAll("{receiverName}", vars.receiverName ?? "")
        .replaceAll("{senderName}", vars.senderName ?? "")
        .replaceAll("{message}", vars.message ?? "");

/* Inline CSS for a text style object */
const textStyleToInline = (style) => {
    const s = style || {};
    return {
        color: s.gradient ? undefined : s.color || DEFAULT_TEXT,
        fontSize: (s.fontSize ?? 24) + "px",
        fontWeight: s.fontWeight ?? 600,
        lineHeight: s.lineHeight ?? 1.2,
        letterSpacing:
            typeof s.letterSpacing === "number" ? s.letterSpacing + "px" : undefined,
        fontStyle: s.italic ? "italic" : "normal",
        textTransform: s.uppercase ? "uppercase" : "none",
        textShadow: s.shadow ? "0 2px 12px rgba(0,0,0,.20)" : undefined,
    };
};

const alignToClass = (align) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

/* ============================= API Normalizers ============================= */
const parseMaybeJSON = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
        try {
            return JSON.parse(v);
        } catch {
            return null;
        }
    }
    return v;
};

/** Normalize API item -> UI item (with safe defaults) */
const fromApi = (t) => {
    const bgGifRaw = t.background_gif ?? t.backgroundGif ?? "";
    const bgGif =
        Array.isArray(bgGifRaw)
            ? bgGifRaw[bgGifRaw.length - 1]
            : (() => {
                try {
                    const parsed = JSON.parse(bgGifRaw);
                    return Array.isArray(parsed) ? parsed[parsed.length - 1] : bgGifRaw;
                } catch {
                    return bgGifRaw;
                }
            })();

    return {
        id: String(t.id ?? ""),
        type: t.type || "birthday",
        name: t.name || "",
        description: t.description ?? "",
        backgroundGif: bgGif || "",
        backgroundColor: t.background_color ?? t.backgroundColor ?? DEFAULT_BG_GRAD,
        textColor: t.text_color ?? t.textColor ?? DEFAULT_TEXT,
        accentColor: t.accent_color ?? t.accentColor ?? DEFAULT_ACCENT_GRAD,
        bgOpacity:
            typeof t.background_opacity === "number"
                ? t.background_opacity
                : typeof t.bg_opacity === "number"
                    ? t.bg_opacity
                    : t.bgOpacity ?? 0.28,
        bgBlurAmount:
            typeof t.background_blur === "number"
                ? t.background_blur
                : typeof t.bg_blur_amount === "number"
                    ? t.bg_blur_amount
                    : t.bgBlurAmount ?? 0,
        layout: Array.isArray(t.layout) ? t.layout : parseMaybeJSON(t.layout) || defaultLayout(),
        createdAt: t.created_at || new Date().toISOString(),
    };
};

/** Group a flat list into { [type]: Template[] } */
const groupByType = (list) =>
    list.reduce((acc, raw) => {
        const t = fromApi(raw);
        (acc[t.type] ||= []).push(t);
        return acc;
    }, {});

/* ============================ Text rows payload helpers ============================ */
const toTextPayload = (templateId, b) => ({
    wish_template_id: templateId,
    block_type: b.type,
    default_text: b.text,
    color: b.style?.color ?? "#1f2937",
    gradient: b.style?.gradient ?? null,
    font_size: b.style?.fontSize ?? 24,
    weight: b.style?.fontWeight ?? 600,
    rotate: b.rotate ?? 0,
    align: b.style?.textAlign ?? "left",
    width: b.w ?? 100,
    italic: !!b.style?.italic,
    uppercase: !!b.style?.uppercase,
    shadow: !!b.style?.shadow,
    position_x: typeof b.x === "number" ? Math.round(b.x * 100) / 100 : 0,
    position_y: typeof b.y === "number" ? Math.round(b.y * 100) / 100 : 0,
});

const saveTextBlocks = async (templateId, blocks, token) => {
    if (!Array.isArray(blocks) || !blocks.length) return { ok: true, failures: 0 };

    const results = await Promise.allSettled(
        blocks.map((b) =>
            postRequest({
                endpoint: "/api/wish-template-text",
                token,
                body: toTextPayload(templateId, b),
            })
        )
    );

    const failures = results.filter(
        (r) =>
            r.status === "rejected" ||
            (r.value && (r.value.ok === false || r.value?.data?.error))
    );

    return { ok: failures.length === 0, failures: failures.length };
};

/* ============================ Layout Designer ============================ */
const LayoutDesigner = ({
    tpl,
    setTpl,
    previewVars = {
        receiverName: "Receiver",
        senderName: "Sender",
        message: "Your message goes here…",
    },
    baseWidth = 800,
    baseHeight = 1200,
}) => {
    const canvasRef = useRef(null);
    const [selectedId, setSelectedId] = useState(() =>
        tpl.layout.length ? tpl.layout[0].id : null
    );
    const [dragState, setDragState] = useState(null);
    const [resizeState, setResizeState] = useState(null);

    const selectedBlock = useMemo(
        () => tpl.layout.find((b) => b.id === selectedId) || null,
        [tpl.layout, selectedId]
    );

    const selectBlock = (id) => setSelectedId(id);

    const updateBlock = (id, patch) => {
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.map((b) => (b.id === id ? { ...b, ...patch } : b)),
        }));
    };

    const updateBlockStyle = (id, stylePatch) => {
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.map((b) =>
                b.id === id ? { ...b, style: { ...(b.style || {}), ...stylePatch } } : b
            ),
        }));
    };

    const addBlock = (kind) => {
        const basics = {
            id: uid(),
            type: kind,
            text:
                kind === "receiver"
                    ? "Dear {receiverName}"
                    : kind === "sender"
                        ? "{senderName}"
                        : kind === "message"
                            ? '"{message}"'
                            : "New text",
            x: 10,
            y: 10 + Math.random() * 60,
            w: 60,
            rotate: 0,
            style: {
                color: tpl.textColor || DEFAULT_TEXT,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1.2,
                textAlign: "left",
                italic: false,
                uppercase: false,
                shadow: false,
            },
        };
        setTpl((prev) => ({ ...prev, layout: [...prev.layout, basics] }));
        setSelectedId(basics.id);
    };

    const removeSelected = () => {
        if (!selectedBlock) return;
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.filter((b) => b.id !== selectedBlock.id),
        }));
        setSelectedId(null);
    };

    const onMouseDownBlock = (e, b) => {
        if (!canvasRef.current) return;
        e.stopPropagation();
        const rect = canvasRef.current.getBoundingClientRect();
        setDragState({
            id: b.id,
            startX: e.clientX,
            startY: e.clientY,
            origX: b.x,
            origY: b.y,
            rect,
        });
    };

    const onMouseMove = useCallback(
        (e) => {
            if (dragState && canvasRef.current) {
                const { startX, startY, origX, origY, rect, id } = dragState;
                const dx = ((e.clientX - startX) / rect.width) * 100;
                const dy = ((e.clientY - startY) / rect.height) * 100;
                const nx = clamp(origX + dx, 0, 100);
                const ny = clamp(origY + dy, 0, 100);
                updateBlock(id, { x: nx, y: ny });
            }
            if (resizeState && canvasRef.current) {
                const { startX, origW, rect, id } = resizeState;
                const dx = ((e.clientX - startX) / rect.width) * 100;
                const nw = clamp(origW + dx, 5, 100);
                updateBlock(id, { w: nw });
            }
        },
        [dragState, resizeState]
    );

    const onMouseUp = useCallback(() => {
        setDragState(null);
        setResizeState(null);
    }, []);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    const startResize = (e, b) => {
        if (!canvasRef.current) return;
        e.stopPropagation();
        const rect = canvasRef.current.getBoundingClientRect();
        setResizeState({
            id: b.id,
            startX: e.clientX,
            origW: b.w,
            rect,
        });
    };

    const resetLayout = () =>
        setTpl((p) => ({
            ...p,
            layout: defaultLayout(),
        }));

    return (
        <div className="space-y-4">
            {/* Template-level styling */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 w-36">Text color</label>
                    <input
                        type="color"
                        className="h-9 w-14 rounded border"
                        value={
                            (selectedBlock?.style?.gradient
                                ? tpl.textColor || DEFAULT_TEXT
                                : selectedBlock?.style?.color) || tpl.textColor || DEFAULT_TEXT
                        }
                        onChange={(e) => {
                            const next = e.target.value;
                            if (selectedBlock) {
                                updateBlockStyle(selectedBlock.id, {
                                    color: next,
                                    gradient: undefined,
                                });
                            } else {
                                setTpl((p) => ({ ...p, textColor: next }));
                            }
                        }}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 w-36">BG opacity</label>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={tpl.bgOpacity ?? 0.28}
                        onChange={(e) =>
                            setTpl((p) => ({ ...p, bgOpacity: parseFloat(e.target.value) }))
                        }
                    />
                    <span className="text-xs text-gray-500 w-10 text-right">
                        {(tpl.bgOpacity ?? 0.28).toFixed(2)}
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 w-36">BG blur (px)</label>
                    <input
                        type="range"
                        min={0}
                        max={12}
                        step={1}
                        value={tpl.bgBlurAmount ?? 0}
                        onChange={(e) =>
                            setTpl((p) => ({ ...p, bgBlurAmount: parseInt(e.target.value, 10) }))
                        }
                    />
                    <span className="text-xs text-gray-500 w-10 text-right">
                        {tpl.bgBlurAmount ?? 0}
                    </span>
                </div>
            </div>

            {/* Canvas */}
            <div className="relative w-full overflow-auto rounded-xl border">
                <div className="min-w-[360px] flex items-center justify-center p-4">
                    <div
                        ref={canvasRef}
                        className={`relative rounded-xl shadow overflow-hidden bg-gradient-to-br ${tpl.backgroundColor}`}
                        style={{ width: baseWidth, height: baseHeight }}
                        onMouseDown={() => setSelectedId(null)}
                    >
                        {(tpl.bgLocalPreview || tpl.backgroundGif) && (
                            <img
                                src={tpl.bgLocalPreview || tpl.backgroundGif}
                                alt="Background"
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{
                                    opacity: tpl.bgOpacity ?? 0.28,
                                    filter:
                                        (tpl.bgBlurAmount ?? 0) > 0
                                            ? `blur(${tpl.bgBlurAmount}px)`
                                            : undefined,
                                }}
                            />
                        )}

                        {tpl.layout.map((b) => {
                            const isSel = b.id === selectedId;
                            const left = `${b.x}%`;
                            const top = `${b.y}%`;
                            const width = `${b.w}%`;
                            const alignCls = alignToClass(b.style?.textAlign);
                            const styleInline = textStyleToInline({
                                ...b.style,
                                color: b.style?.color || tpl.textColor || DEFAULT_TEXT,
                            });
                            const text = resolveText(b.text, previewVars);

                            return (
                                <div
                                    key={b.id}
                                    className={`absolute ${alignCls}`}
                                    style={{
                                        left,
                                        top,
                                        width,
                                        transform: `rotate(${b.rotate || 0}deg)`,
                                        cursor: "move",
                                    }}
                                    onMouseDown={(e) => onMouseDownBlock(e, b)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        selectBlock(b.id);
                                    }}
                                >
                                    <span
                                        className={`${b.style?.gradient
                                            ? `bg-gradient-to-r ${b.style.gradient} bg-clip-text text-transparent`
                                            : ""
                                            } ${isSel ? "ring-2 ring-indigo-500 rounded" : ""} inline-block`}
                                        style={styleInline}
                                    >
                                        {text}
                                    </span>

                                    {/* Resize handle */}
                                    <div
                                        onMouseDown={(e) => startResize(e, b)}
                                        className={`absolute -right-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full ${isSel ? "bg-indigo-500" : "bg-gray-300"
                                            } shadow cursor-ew-resize`}
                                        title="Drag to resize width"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Add block buttons + reset */}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => addBlock("heading")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Heading
                </button>
                <button
                    type="button"
                    onClick={() => addBlock("subheading")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Subheading
                </button>
                <button
                    type="button"
                    onClick={() => addBlock("message")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Message
                </button>
                <button
                    type="button"
                    onClick={() => addBlock("receiver")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Receiver
                </button>
                <button
                    type="button"
                    onClick={() => addBlock("sender")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Sender
                </button>
                <button
                    type="button"
                    onClick={() => addBlock("custom")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Custom Text
                </button>

                <div className="flex-1" />
                <button
                    type="button"
                    onClick={resetLayout}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                    title="Reset layout"
                >
                    <RotateCcw className="w-4 h-4" /> Reset
                </button>
            </div>

            {/* Inspector */}
            <div className="bg-white rounded-xl border p-3">
                <h4 className="text-sm font-semibold mb-2">Inspector</h4>
                {!selectedBlock ? (
                    <p className="text-sm text-gray-500">
                        Select a text block to edit its properties.
                    </p>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Block type</span>
                            <span className="px-2 py-1 rounded bg-gray-100 text-xs">
                                {selectedBlock.type}
                            </span>
                        </div>

                        <label className="block text-sm text-gray-600 mb-1">Text</label>
                        <textarea
                            className="w-full rounded border px-3 py-2 text-sm mb-3"
                            rows={3}
                            value={selectedBlock.text}
                            onChange={(e) => updateBlock(selectedBlock.id, { text: e.target.value })}
                            placeholder='Use {receiverName}, {senderName}, {message}'
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Color</label>
                                <input
                                    type="color"
                                    className="h-9 w-14 rounded border"
                                    value={selectedBlock.style?.color || tpl.textColor || DEFAULT_TEXT}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, {
                                            color: e.target.value,
                                            gradient: undefined,
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Font size</label>
                                <input
                                    type="number"
                                    className="w-full rounded border px-2 py-2 text-sm"
                                    min={8}
                                    max={200}
                                    value={selectedBlock.style?.fontSize ?? 28}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, {
                                            fontSize: parseInt(e.target.value || "0", 10),
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Weight</label>
                                <select
                                    className="w-full rounded border px-2 py-2 text-sm"
                                    value={selectedBlock.style?.fontWeight ?? 700}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, {
                                            fontWeight: parseInt(e.target.value, 10),
                                        })
                                    }
                                >
                                    {[300, 400, 500, 600, 700, 800, 900].map((w) => (
                                        <option key={w} value={w}>
                                            {w}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Align</label>
                                <select
                                    className="w-full rounded border px-2 py-2 text-sm"
                                    value={selectedBlock.style?.textAlign ?? "left"}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, { textAlign: e.target.value })
                                    }
                                >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Rotate (deg)</label>
                                <input
                                    type="number"
                                    className="w-full rounded border px-2 py-2 text-sm"
                                    value={selectedBlock.rotate ?? 0}
                                    onChange={(e) =>
                                        updateBlock(selectedBlock.id, {
                                            rotate: parseInt(e.target.value || "0", 10),
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Width (%)</label>
                                <input
                                    type="number"
                                    className="w-full rounded border px-2 py-2 text-sm"
                                    min={5}
                                    max={100}
                                    value={selectedBlock.w}
                                    onChange={(e) =>
                                        updateBlock(selectedBlock.id, {
                                            w: clamp(parseFloat(e.target.value || "0"), 5, 100),
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 mt-3">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={!!selectedBlock.style?.italic}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, { italic: e.target.checked })
                                    }
                                />
                                Italic
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={!!selectedBlock.style?.uppercase}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, {
                                            uppercase: e.target.checked,
                                        })
                                    }
                                />
                                Uppercase
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={!!selectedBlock.style?.shadow}
                                    onChange={(e) =>
                                        updateBlockStyle(selectedBlock.id, { shadow: e.target.checked })
                                    }
                                />
                                Shadow
                            </label>
                        </div>

                        <div className="mt-3">
                            <label className="block text-sm text-gray-600 mb-1">
                                Gradient (Tailwind class)
                            </label>
                            <input
                                className="w-full rounded border px-3 py-2 text-sm"
                                placeholder="from-pink-500 via-purple-500 to-blue-500"
                                value={selectedBlock.style?.gradient || ""}
                                onChange={(e) =>
                                    updateBlockStyle(selectedBlock.id, {
                                        gradient: e.target.value || undefined,
                                    })
                                }
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Leave empty to use solid color. When set, text uses gradient with{" "}
                                <code>bg-clip-text text-transparent</code>.
                            </p>
                        </div>

                        <div className="mt-4 flex justify-between">
                            <button
                                type="button"
                                onClick={removeSelected}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm"
                            >
                                <Trash2 className="w-4 h-4" /> Remove block
                            </button>
                            <div className="text-xs text-gray-500">
                                Drag on canvas to move. Use the right dot to resize width.
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/* ============================ Template Manager ============================ */
const TemplateManagerPage = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState({});
    const [selectedType, setSelectedType] = useState("birthday");
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState(null);

    const wishTypes = [
        { id: "birthday", name: "🎂 Birthday", color: "from-pink-500 to-purple-500" },
        { id: "anniversary", name: "❤️ Anniversary", color: "from-red-500 to-pink-500" },
        { id: "congratulations", name: "🎉 Congratulations", color: "from-yellow-500 to-orange-500" },
        { id: "retirement", name: "🏆 Retirement", color: "from-blue-500 to-indigo-500" },
        { id: "graduation", name: "🎓 Graduation", color: "from-purple-500 to-blue-500" },
        { id: "wedding", name: "💒 Wedding", color: "from-pink-500 to-rose-500" },
        { id: "baby", name: "👶 Baby Shower", color: "from-blue-400 to-pink-400" },
        { id: "love", name: "💕 Love Note", color: "from-red-500 to-purple-500" },
    ];

    const refreshTemplates = async (token) => {
        try {
            const res = await loadTemplates(token);
            const items = Array.isArray(res) ? res : res?.wishtemplates || res?.wishTemplates || [];
            const grouped = groupByType(items);
            setTemplates(grouped);
            if (!grouped[selectedType]) {
                const first = Object.keys(grouped)[0];
                if (first) setSelectedType(first);
            }
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const u = await getUser();
                setUser(u);
                await refreshTemplates(u.token);
            } catch {
                setUser(null);
            }
        })();
    }, []);

    const currentTemplates = templates[selectedType] || [];
    const totalTemplates = Object.values(templates).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0
    );

    const deleteTemplate = async (id, type) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return;
        if (!user?.token) {
            toast.error("You must be logged in to delete templates.", {
                position: "bottom-center",
                autoClose: 3000,
            });
            return;
        }

        setDeletingId(String(id));

        try {
            const { status, data } = await deleteRequest(`/api/wish-template/${id}`, user.token);

            if (status === 200 && !data?.error) {
                setTemplates((prev) => {
                    const next = { ...prev };
                    next[type] = (next[type] || []).filter((t) => String(t.id) !== String(id));
                    return next;
                });
                await refreshTemplates(user.token);
                toast.success("Successfully deleted Template", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
            } else {
                toast.error(data?.message || "Failed to delete template.", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
            }
        } catch {
            toast.error("Network or server error.", {
                position: "bottom-center",
                autoClose: 3000,
            });
        } finally {
            setDeletingId(null);
        }
    };

    /* ---------- Create: create template meta, then create text rows ---------- */
    const createTemplate = async (templateData) => {
        if (!user?.token) {
            toast.error("You must be logged in to create templates.", {
                position: "bottom-center",
                autoClose: 3000,
            });
            return;
        }
        setIsLoading(true);

        try {
            // 1) Create template meta
            const form = new FormData();
            form.append("type", templateData.type);
            form.append("name", templateData.name);
            form.append("description", templateData.description);
            form.append("background_color", templateData.backgroundColor);
            form.append("text_color", templateData.textColor);
            form.append("accent_color", templateData.accentColor);
            // match your backend names (also supports bg_opacity/bg_blur_amount if needed)
            form.append("background_opacity", String(templateData.bgOpacity ?? 0.28));
            form.append("background_blur", String(templateData.bgBlurAmount ?? 0));
            if (templateData.backgroundFile) {
                form.append("background_gif", templateData.backgroundFile);
            }

            const { data, status } = await uploadFile(form, "/api/wish-template", user.token);
            if (status !== 200 || data?.error) {
                toast.error(data?.message || "Failed to create template.", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
                setIsLoading(false);
                return;
            }

            const created = data.wishTemplate || data.wishtemplate;
            const templateId = created?.id;
            console.log("Created template", templateId);
            if (!templateId) {
                toast.error("Template created but no ID returned.", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
                setIsLoading(false);
                return;
            }

            // 2) Create all text rows for this template
            const saveRes = await saveTextBlocks(templateId, templateData.layout, user.token);
            if (!saveRes.ok) {
                toast.warn(`Template saved but ${saveRes.failures} text blocks failed to save.`, {
                    position: "bottom-center",
                    autoClose: 4000,
                });
            }

            // 3) Refresh UI
            await refreshTemplates(user.token);
            if (selectedType !== created.type) setSelectedType(created.type);

            toast.success("Template created successfully.", {
                position: "bottom-center",
                autoClose: 3000,
            });
            setShowCreateModal(false);
        } catch {
            toast.error("Network or server error.", {
                position: "bottom-center",
                autoClose: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    /* ---------- Update: update template meta (texts not synchronized here) ---------- */
    const updateTemplate = async (oldType, templateId, templateData) => {
        if (!user?.token) {
            toast.error("You must be logged in to update templates.", {
                position: "bottom-center",
                autoClose: 3000,
            });
            return;
        }
        setIsLoading(true);

        try {
            const form = new FormData();
            form.append("type", templateData.type);
            form.append("name", templateData.name);
            form.append("description", templateData.description);
            form.append("background_color", templateData.backgroundColor);
            form.append("text_color", templateData.textColor);
            form.append("accent_color", templateData.accentColor);
            form.append("background_opacity", String(templateData.bgOpacity ?? 0.28));
            form.append("background_blur", String(templateData.bgBlurAmount ?? 0));
            if (templateData.backgroundFile) {
                form.append("background_gif", templateData.backgroundFile);
            }

            const { data, status } = await uploadFile(
                form,
                `/api/wish-template/update/${templateId}`,
                user.token
            );

            if (status === 200 && !data.error) {
                const saved = fromApi(data.wishTemplate || data.wishtemplate || {});
                setTemplates((prev) => {
                    const next = { ...prev };
                    next[oldType] = (next[oldType] || []).filter((t) => t.id !== String(templateId));
                    (next[saved.type] ||= []).push(saved);
                    return next;
                });

                if (selectedType !== saved.type) setSelectedType(saved.type);

                toast.success("Template updated successfully.", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
                setEditingTemplate(null);
            } else {
                toast.error(data?.message || "Failed to update template.", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
            }
        } catch {
            toast.error("Network or server error.", {
                position: "bottom-center",
                autoClose: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate("/portal/qrs")}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Template Manager</h1>
                        <p className="text-gray-600">
                            Create and manage custom templates for your wishes
                        </p>
                    </div>
                </div>

                {/* Stats Cards */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
                >
                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center">
                                <Palette className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Total Templates</p>
                                <p className="text-2xl font-bold text-gray-800">{totalTemplates}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center">
                                <Eye className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Active Type</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {currentTemplates.length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center">
                                <div className="w-6 h-6 text-purple-600 font-bold text-sm">
                                    <CircleStar className="w-6 h-6 text-purple-600" />
                                </div>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Wish Types</p>
                                <p className="text-2xl font-bold text-gray-800">{wishTypes.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="w-full h-full flex items-center justify-center gap-2 text-gradient-secondary hover:bg-orange-50 rounded-lg transition-colors"
                        >
                            <Plus className="w-6 h-6 text-gradient-secondary" />
                            <span className="font-semibold">New Template</span>
                        </button>
                    </div>
                </motion.div>

                {/* Wish Type Selector */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-2xl shadow-lg p-6 mb-8"
                >
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                        Select Wish Type
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {wishTypes.map((type) => (
                            <motion.button
                                key={type.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setSelectedType(type.id)}
                                className={`p-4 rounded-xl font-semibold transition-all duration-300 ${selectedType === type.id
                                    ? `bg-gradient-to-r ${type.color} text-white shadow-lg`
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    }`}
                            >
                                {type.name}
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                {/* Templates Grid */}
                {currentTemplates.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-2xl shadow-lg p-8"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-800">
                                {wishTypes.find((t) => t.id === selectedType)?.name} Templates
                            </h2>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="btn gradient-secondary text-white px-4 py-3 rounded-3 fw-semibold d-flex align-items-center gap-2 shadow text-decoration-none"
                            >
                                <Plus className="w-4 h-4" />
                                Add Template
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {currentTemplates.map((template, index) => (
                                <motion.div
                                    key={template.id}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                                >
                                    <div
                                        className={`h-32 bg-gradient-to-br ${template.backgroundColor} relative overflow-hidden`}
                                    >
                                        {template.backgroundGif && (
                                            <img
                                                src={template.backgroundGif}
                                                alt="Background"
                                                className="w-full h-full object-cover opacity-30"
                                            />
                                        )}

                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div
                                                className={`bg-gradient-to-r ${template.accentColor} rounded-full w-12 h-12 flex items-center justify-center`}
                                            >
                                                <span className="text-white text-xl">
                                                    {wishTypes.find((t) => t.id === selectedType)?.name.split(" ")[0]}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">
                                            {template.name}
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-3">
                                            {template.description}
                                        </p>
                                        <p className="text-xs text-gray-500 mb-4">
                                            Created {new Date(template.createdAt).toLocaleDateString()}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setPreviewTemplate(template)}
                                                className="flex-1 bg-blue-500 text-white px-3 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors text-sm flex items-center justify-center gap-1"
                                            >
                                                <Eye className="w-4 h-4" />
                                                Preview
                                            </button>
                                            <button
                                                onClick={() => setEditingTemplate(template)}
                                                className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors"
                                                title="Edit"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>

                                            <button
                                                onClick={() => deleteTemplate(template.id, template.type)}
                                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2 disabled:opacity-70"
                                                title="Delete"
                                                disabled={deletingId === String(template.id)}
                                            >
                                                {deletingId === String(template.id) ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                ) : (
                                                    <Trash2 className="w-4 h-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-2xl shadow-lg p-12 text-center"
                    >
                        <div className="text-6xl mb-4">🎨</div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">
                            No Templates for {wishTypes.find((t) => t.id === selectedType)?.name}
                        </h3>
                        <p className="text-gray-500 mb-6">
                            Create your first custom template for this wish type
                        </p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center gap-2 bg-gradient-to-r gradient-secondary text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-transform duration-300"
                        >
                            <Plus className="w-5 h-5" />
                            Create First Template
                        </button>
                    </motion.div>
                )}
            </div>

            {/* Create/Edit Template Modal */}
            {(showCreateModal || editingTemplate) && (
                <TemplateModal
                    isOpen={true}
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingTemplate(null);
                    }}
                    onSave={
                        editingTemplate
                            ? (data) => updateTemplate(editingTemplate.type, editingTemplate.id, data)
                            : createTemplate
                    }
                    template={editingTemplate}
                    saving={isLoading}
                />
            )}

            {/* Preview Modal (simple) */}
            {previewTemplate && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-lg font-semibold">{previewTemplate.name}</h3>
                            <button
                                onClick={() => setPreviewTemplate(null)}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <div
                                className={`w-full h-64 bg-gradient-to-br ${previewTemplate.backgroundColor} rounded-lg relative overflow-hidden mb-4`}
                            >
                                {previewTemplate.backgroundGif && (
                                    <img
                                        src={
                                            typeof previewTemplate.backgroundGif === "string"
                                                ? previewTemplate.backgroundGif
                                                : previewTemplate.backgroundGif.url
                                        }
                                        alt="Background"
                                        className="w-full h-full object-cover opacity-30"
                                    />
                                )}

                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div
                                        className={`bg-gradient-to-r ${previewTemplate.accentColor} rounded-full w-16 h-16 flex items-center justify-center`}
                                    >
                                        <span className="text-white text-2xl">
                                            {wishTypes.find((t) => t.id === selectedType)?.name.split(" ")[0]}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                <div>
                                    <strong>Description:</strong> {previewTemplate.description}
                                </div>
                                <div>
                                    <strong>Created:</strong>{" "}
                                    {new Date(previewTemplate.createdAt).toLocaleString()}
                                </div>
                                <div>
                                    <strong>Background:</strong> {previewTemplate.backgroundColor}
                                </div>
                                <div>
                                    <strong>Accent:</strong> {previewTemplate.accentColor}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

const TemplateModal = ({ isOpen, onClose, onSave, template, saving = false }) => {
    const wishTypes = [
        { value: "birthday", label: "🎂 Birthday", description: "Celebrate another year of life" },
        { value: "anniversary", label: "❤️ Anniversary", description: "Commemorate special milestones" },
        { value: "congratulations", label: "🎉 Congratulations", description: "Celebrate achievements" },
        { value: "retirement", label: "🏆 Retirement", description: "Honor years of service" },
        { value: "graduation", label: "🎓 Graduation", description: "Academic accomplishments" },
        { value: "wedding", label: "💒 Wedding", description: "Celebrate new beginnings" },
        { value: "baby", label: "👶 Baby Shower", description: "Welcome the little one" },
        { value: "love", label: "💕 Love Note", description: "Express your feelings" },
    ];

    const panelRef = React.useRef(null);

    const [tpl, setTpl] = React.useState(() =>
        template
            ? fromApi(template)
            : {
                id: "",
                type: "birthday",
                name: "",
                description: "",
                backgroundGif: "",
                backgroundFile: null,
                backgroundColor: DEFAULT_BG_GRAD,
                textColor: DEFAULT_TEXT,
                accentColor: DEFAULT_ACCENT_GRAD,
                bgOpacity: 0.28,
                bgBlurAmount: 0,
                layout: defaultLayout(),
                bgLocalPreview: "",
            }
    );

    const [previewVars, setPreviewVars] = React.useState({
        receiverName: "Alex",
        senderName: "Sam",
        message: "Have an amazing day!",
    });

    const fileInputRef = React.useRef(null);

    const backgroundOptions = [
        { name: "Soft Pink", value: "from-pink-100 via-purple-50 to-blue-100" },
        { name: "Warm Orange", value: "from-yellow-100 via-orange-50 to-pink-100" },
        { name: "Cool Blue", value: "from-blue-100 via-cyan-50 to-teal-100" },
        { name: "Romantic Red", value: "from-red-100 via-pink-50 to-purple-100" },
        { name: "Nature Green", value: "from-emerald-100 via-teal-100 to-cyan-100" },
        { name: "Royal Purple", value: "from-purple-100 via-indigo-50 to-blue-100" },
    ];

    const accentOptions = [
        { name: "Pink to Purple", value: "from-pink-500 to-purple-500" },
        { name: "Orange to Red", value: "from-orange-500 to-red-500" },
        { name: "Blue to Cyan", value: "from-blue-500 to-cyan-500" },
        { name: "Green to Teal", value: "from-green-500 to-teal-500" },
        { name: "Yellow to Orange", value: "from-yellow-500 to-orange-500" },
        { name: "Purple to Pink", value: "from-purple-500 to-pink-500" },
    ];

    const handleFilePick = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setTpl((p) => ({ ...p, backgroundFile: file, bgLocalPreview: url }));
    };

    const clearBackground = () => {
        setTpl((p) => ({
            ...p,
            backgroundFile: null,
            bgLocalPreview: "",
            backgroundGif: "",
        }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (saving) return;
        const payload = { ...tpl };
        delete payload.bgLocalPreview; // editor-only
        onSave(payload);
    };

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape" && !saving) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, saving, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" style={{ overflow: "auto" }}>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={saving ? undefined : onClose}
            />

            <div
                className="relative z-10 flex min-h-full items-start sm:items-center justify-center p-4"
                onMouseDown={(e) => {
                    if (saving) return;
                    if (panelRef.current && !panelRef.current.contains(e.target)) {
                        onClose();
                    }
                }}
            >
                <motion.div
                    ref={panelRef}
                    onMouseDown={(e) => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="pointer-events-auto w-full max-w-6xl rounded-2xl bg-white shadow-xl overflow-hidden
               h-[90vh] max-h-[92svh] grid grid-rows-[auto_1fr_auto]"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b bg-white">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-gray-800">
                                {template ? "Edit Template" : "Create New Template"}
                            </h2>
                            <button
                                type="button"
                                onClick={onClose}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                                disabled={saving}
                                aria-disabled={saving}
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="contents" aria-busy={saving}>
                        <div
                            className="px-6 py-6 overflow-y-auto min-h-0 bg-white touch-pan-y overscroll-contain"
                            style={{ WebkitOverflowScrolling: "touch", overflow: "auto" }}
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Left column (meta & GIF uploader) */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Template Type
                                        </label>
                                        <select
                                            value={tpl.type}
                                            onChange={(e) => setTpl((p) => ({ ...p, type: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            required
                                        >
                                            {wishTypes.map((t) => (
                                                <option key={t.value} value={t.value}>
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {{
                                                birthday: "Celebrate another year of life",
                                                anniversary: "Commemorate special milestones",
                                                congratulations: "Celebrate achievements",
                                                retirement: "Honor years of service",
                                                graduation: "Academic accomplishments",
                                                wedding: "Celebrate new beginnings",
                                                baby: "Welcome the little one",
                                                love: "Express your feelings",
                                            }[tpl.type]}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Template Name
                                        </label>
                                        <input
                                            type="text"
                                            value={tpl.name}
                                            onChange={(e) => setTpl((p) => ({ ...p, name: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            placeholder="Enter template name..."
                                            required
                                            disabled={saving}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Description
                                        </label>
                                        <textarea
                                            value={tpl.description}
                                            onChange={(e) => setTpl((p) => ({ ...p, description: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                                            rows={3}
                                            placeholder="Describe your template..."
                                            required
                                            disabled={saving}
                                        />
                                    </div>

                                    {/* Background GIF uploader */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Background GIF (optional)
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/gif,image/webp,video/mp4,image/*"
                                                onChange={(e) => handleFilePick(e.target.files?.[0])}
                                                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer focus:outline-none"
                                                disabled={saving}
                                            />
                                            {(tpl.bgLocalPreview || tpl.backgroundGif) && (
                                                <button
                                                    type="button"
                                                    onClick={clearBackground}
                                                    className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                                                    disabled={saving}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>

                                        {(tpl.bgLocalPreview || tpl.backgroundGif) && (
                                            <div className="mt-2">
                                                <div className="relative h-32 rounded-lg overflow-hidden border bg-white">
                                                    <img
                                                        src={tpl.bgLocalPreview || tpl.backgroundGif}
                                                        alt="Preview"
                                                        className="absolute inset-0 w-full h-full object-cover"
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    This media overlays the gradient with opacity/blur set below.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right column (color pickers & quick preview) */}
                                <div className="space-y-6">
                                    <ColorPickers
                                        label="Background gradient"
                                        options={backgroundOptions}
                                        value={tpl.backgroundColor}
                                        onPick={(val) => setTpl((p) => ({ ...p, backgroundColor: val }))}
                                        saving={saving}
                                    />

                                    <ColorPickers
                                        label="Accent gradient"
                                        options={accentOptions}
                                        value={tpl.accentColor}
                                        onPick={(val) => setTpl((p) => ({ ...p, accentColor: val }))}
                                        saving={saving}
                                    />

                                    {/* BG opacity & blur controls */}
                                    <div className="rounded-xl border p-3 space-y-3 bg-white">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm text-gray-600 w-28">BG opacity</label>
                                            <input
                                                type="range"
                                                min={0}
                                                max={1}
                                                step={0.01}
                                                value={tpl.bgOpacity ?? 0.28}
                                                onChange={(e) =>
                                                    setTpl((p) => ({ ...p, bgOpacity: parseFloat(e.target.value) }))
                                                }
                                            />
                                            <span className="text-xs text-gray-500 w-10 text-right">
                                                {(tpl.bgOpacity ?? 0.28).toFixed(2)}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <label className="text-sm text-gray-600 w-28">BG blur (px)</label>
                                            <input
                                                type="range"
                                                min={0}
                                                max={12}
                                                step={1}
                                                value={tpl.bgBlurAmount ?? 0}
                                                onChange={(e) =>
                                                    setTpl((p) => ({
                                                        ...p,
                                                        bgBlurAmount: parseInt(e.target.value, 10),
                                                    }))
                                                }
                                            />
                                            <span className="text-xs text-gray-500 w-10 text-right">
                                                {tpl.bgBlurAmount ?? 0}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Quick preview */}
                                    <div className="rounded-xl border p-3 bg-white">
                                        <div
                                            className={`h-40 rounded-lg relative overflow-hidden bg-gradient-to-br ${tpl.backgroundColor}`}
                                        >
                                            {(tpl.bgLocalPreview || tpl.backgroundGif) && (
                                                <img
                                                    src={tpl.bgLocalPreview || tpl.backgroundGif}
                                                    alt="Background"
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                    style={{
                                                        opacity: tpl.bgOpacity ?? 0.28,
                                                        filter:
                                                            (tpl.bgBlurAmount ?? 0) > 0
                                                                ? `blur(${tpl.bgBlurAmount}px)`
                                                                : undefined,
                                                    }}
                                                />
                                            )}

                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${tpl.accentColor}`} />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 text-xs text-gray-600 mt-3">
                                            <div>
                                                <strong>BG:</strong> {tpl.backgroundColor}
                                            </div>
                                            <div>
                                                <strong>Accent:</strong> {tpl.accentColor}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Preview variables */}
                                    <div className="grid grid-cols-3 gap-2">
                                        <input
                                            className="border rounded-lg px-2 py-2 text-sm bg-white"
                                            placeholder="Receiver"
                                            value={previewVars.receiverName}
                                            onChange={(e) =>
                                                setPreviewVars((p) => ({ ...p, receiverName: e.target.value }))
                                            }
                                        />
                                        <input
                                            className="border rounded-lg px-2 py-2 text-sm bg-white"
                                            placeholder="Sender"
                                            value={previewVars.senderName}
                                            onChange={(e) =>
                                                setPreviewVars((p) => ({ ...p, senderName: e.target.value }))
                                            }
                                        />
                                        <input
                                            className="border rounded-lg px-2 py-2 text-sm col-span-3 lg:col-span-1 bg-white"
                                            placeholder="Message"
                                            value={previewVars.message}
                                            onChange={(e) =>
                                                setPreviewVars((p) => ({ ...p, message: e.target.value }))
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Layout Designer */}
                            <div className="bg-white rounded-2xl shadow p-4 mt-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold">Text Layout Editor</h3>
                                </div>
                                <LayoutDesigner tpl={tpl} setTpl={setTpl} previewVars={previewVars} />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t bg-white">
                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                                    disabled={saving}
                                    aria-disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="btn gradient-secondary text-white px-4 py-3 rounded-3 fw-semibold d-flex align-items-center gap-2 shadow text-decoration-none"
                                    style={{ opacity: saving ? 0.8 : 1 }}
                                >
                                    {saving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            {template ? "Updating..." : "Creating..."}
                                        </>
                                    ) : template ? (
                                        "Update Template"
                                    ) : (
                                        "Create Template"
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

/* Small helper for color pick groups */
const ColorPickers = ({ label, options, value, onPick, saving }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="grid grid-cols-2 gap-3">
            {options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    onClick={() => onPick(option.value)}
                    className={`p-3 rounded-xl border-2 transition-all ${value === option.value
                        ? "border-orange-500 ring-2 ring-orange-200"
                        : "border-gray-200 hover:border-gray-300"
                        }`}
                    disabled={saving}
                >
                    <div className={`w-full h-8 bg-gradient-to-r ${option.value} rounded-lg mb-2`} />
                    <span className="text-sm font-medium">{option.name}</span>
                </button>
            ))}
        </div>
    </div>
);

export default TemplateManagerPage;
